import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Segment, Grid, Header, Icon, Form, Button } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import useWindowSize from "../../utils/useWindowSize";
import {
  getEffectiveCourseDiscount,
  calculateDiscountedPrice,
} from "../../utils/skiworkUtils";
import Mappa from "../common/Mappa";
import InfoWeb from "../common/InfoWeb";
import PrezzoOfferta from "./PrezzoOfferta";

/** Numero massimo di persone acquistabili online per un pacchetto. */
const MAX_PERSONE = 5;

/**
 * Interfaccia di prenotazione di una "Offerta / Pacchetto" (corso con flag CorsoPrivato).
 *
 * Differenze rispetto a un corso collettivo:
 *  - la durata è fissa: sono le giornate della sessione, nessun selettore;
 *  - il giorno di inizio è fisso: se il corso ha più finestre di disponibilità si sceglie il
 *    periodo, altrimenti è in sola lettura. Nessun calendario;
 *  - il listino è indicizzato per numero di persone anziché per giornate (`numGiorni` ==
 *    numero di persone) e il prezzo è già il totale della comitiva.
 */
function DettaglioOfferta({ corso, sessioniCorso, datiScuola, promozioniNew, appData, ...props }) {
  const [idxSessione, setIdxSessione] = useState(0);
  const [numAllievi, setNumAllievi] = useState(1);
  const [coordinateRitrovo, setCoordinateRitrovo] = useState(undefined);
  const [mapVisible, setMapVisible] = useState(false);

  const windowSize = useWindowSize();
  const { t, i18n } = useTranslation([datiScuola.customTranslationNamespace]);

  useEffect(() => {
    i18n.emit("languageChanged");
  }, [datiScuola.customTranslationNamespace]);

  const sessioni = sessioniCorso?.sessioni ?? [];
  const sessioneSelezionata = sessioni[idxSessione];

  // Il pacchetto si acquista per intero: tutte le giornate della sessione.
  const giorniOfferta =
    sessioneSelezionata?.listaDateEffettive?.length ??
    sessioneSelezionata?.numGiorni ??
    0;
  const numSessioniEffettive = sessioneSelezionata?.righe?.length ?? 0;

  /**
   * Persone acquistabili: solo gli slot che la scuola ha effettivamente valorizzato.
   * Senza il filtro sul prezzo, una scuola che ha compilato solo Prz1gg..Prz3gg lascerebbe
   * comprare il pacchetto per 5 persone a 0.
   */
  const numAllieviDisponibili = useMemo(() => {
    const listino = sessioneSelezionata?.listino ?? [];
    return listino
      .filter((x) => x.numGiorni >= 1 && x.numGiorni <= MAX_PERSONE && x.prezzo > 0)
      .sort((a, b) => a.numGiorni - b.numGiorni)
      .map((x) => ({
        key: x.numGiorni,
        text: String(x.numGiorni),
        value: x.numGiorni,
      }));
  }, [sessioneSelezionata]);

  // Tengo il numero di persone dentro a quanto il periodo scelto è realmente prezzato.
  useEffect(() => {
    if (numAllieviDisponibili.length === 0) return;
    if (!numAllieviDisponibili.some((x) => x.value === numAllievi)) {
      setNumAllievi(numAllieviDisponibili[0].value);
    }
  }, [numAllieviDisponibili]);

  // Il ritrovo cambia col periodo, quindi le coordinate vanno ricalcolate a ogni cambio sessione.
  useEffect(() => {
    setMapVisible(false);
    setCoordinateRitrovo(undefined);

    const coordinate = sessioneSelezionata?.coordinateRitrovo;
    if (!coordinate || coordinate.length < 20) return;

    const values = coordinate.split(",");
    if (values.length !== 3) return;

    setCoordinateRitrovo({
      lat: parseFloat(values[0]),
      lng: parseFloat(values[1]),
      z: parseFloat(values[2]),
    });
  }, [idxSessione, sessioneSelezionata]);

  const hasAgency = Boolean(appData?.agenzia);
  const skipPromo = hasAgency && !appData.agenzia.applicaPromozioni;
  // Il pacchetto ha una sola giornata di riferimento: l'inizio della sessione scelta ("dal").
  const firstLessonDate = sessioneSelezionata?.dal;
  const promoPerc = skipPromo
    ? 0
    : getEffectiveCourseDiscount(corso, datiScuola, promozioniNew, firstLessonDate);
  const agencyPerc = hasAgency ? appData?.agenzia?.sconto ?? 0 : 0;

  /** Voce di listino per il numero di persone scelto, con promo e sconto agenzia applicati. */
  function listino() {
    const item = (sessioneSelezionata?.listino ?? []).find(
      (x) => parseInt(numAllievi, 10) === x.numGiorni
    );
    if (!item) return item;

    const roundingMode = promozioniNew?.discountRoundingMode ?? datiScuola.arrotondamentoVIP ?? 0;
    const discountedPrezzo = calculateDiscountedPrice(
      item.prezzo,
      promoPerc,
      agencyPerc,
      roundingMode
    );

    let discountedPrezzoPag = item.prezzoPag;
    if (item.prezzo === item.prezzoPag) {
      discountedPrezzoPag = discountedPrezzo;
    }

    return {
      ...item,
      prezzo: discountedPrezzo,
      prezzoPag: discountedPrezzoPag,
      prezzoListinoRiferimento: item.prezzo,
    };
  }

  const listinoInUso = listino();
  const periodoOptions = sessioni.map((sessione, index) => ({
    key: index,
    value: index,
    text: `${moment(sessione.dal).format("DD/MM/YYYY")} → ${moment(
      sessione.al
    ).format("DD/MM/YYYY")}`,
  }));

  if (!sessioneSelezionata) {
    return (
      <Segment basic textAlign="center">
        {t("nessuna_disponibilita")}
      </Segment>
    );
  }

  return (
    <Segment
      basic
      className="padding_0_lr"
      style={{ marginTop: "0px", paddingTop: "0px", paddingLeft: "0px" }}
    >
      <Grid columns="equal" stackable textAlign="left">
        <Grid.Column
          style={{
            paddingLeft: windowSize.width > 600 ? "0px" : "14px",
            margin: "0px",
            paddingTop: "0px",
          }}
          mobile="16"
          largeScreen="10"
        >
          <Segment>
            <Header as="h3">
              <Icon name="calendar alternate outline"></Icon>
              <Header.Content>{t("programma")}</Header.Content>
            </Header>
            <ul>
              {sessioneSelezionata.righe?.map((riga, index) => (
                <li key={`${riga.rigaStampa}-${index}`}>{riga.rigaStampa}</li>
              ))}
            </ul>
          </Segment>

          <div
            style={{
              marginLeft: windowSize.width > 600 ? "2em" : "2px",
              marginTop: "2em",
            }}
          >
            <InfoWeb
              testi={datiScuola.infoWeb.filter((x) => x.sezione === 0)}
              sezione={0}
            />
          </div>
        </Grid.Column>

        <Grid.Column mobile="16" largeScreen="6">
          <Segment>
            <Header as="h3">
              <Icon name="calendar alternate outline"></Icon>
              <Header.Content>{t("dettagli_corso")}</Header.Content>
            </Header>

            <Form>
              {sessioni.length > 1 ? (
                <Form.Dropdown
                  selection
                  label={t("periodo")}
                  options={periodoOptions}
                  value={idxSessione}
                  onChange={(e, data) => setIdxSessione(data.value)}
                />
              ) : (
                <Form.Input
                  label={t("periodo")}
                  value={`${moment(sessioneSelezionata.dal).format(
                    "DD/MM/YYYY"
                  )} → ${moment(sessioneSelezionata.al).format("DD/MM/YYYY")}`}
                  readOnly
                  fluid
                  onChange={() => {}}
                />
              )}

              <Form.Input
                label={t("durata_pacchetto")}
                value={t("numero_giornate", { count: giorniOfferta })}
                readOnly
                fluid
                onChange={() => {}}
              />

              <Form.Dropdown
                selection
                label={t("numero_persone")}
                options={numAllieviDisponibili}
                value={numAllievi}
                onChange={(e, data) => setNumAllievi(data.value)}
              />

              <div style={{ marginTop: "1em" }}>
                <label>
                  <strong>{t("ritrovo")}</strong>
                </label>
              </div>
              <div>
                <span>{sessioneSelezionata.ritrovo}</span>
                {coordinateRitrovo && (
                  <span style={{ marginLeft: "0.5em" }}>
                    <Icon
                      name="map pin"
                      circular
                      link
                      color="red"
                      onClick={() => setMapVisible(!mapVisible)}
                    ></Icon>
                  </span>
                )}
              </div>
              {mapVisible && coordinateRitrovo && (
                <Mappa
                  ritrovo={t("ritrovo")}
                  lat={coordinateRitrovo.lat}
                  lng={coordinateRitrovo.lng}
                  zoom={coordinateRitrovo.z}
                ></Mappa>
              )}
            </Form>

            <PrezzoOfferta
              listinoInUso={listinoInUso}
              numAllievi={numAllievi}
              datiScuola={datiScuola}
            />
          </Segment>

          <Segment basic textAlign="center">
            <Button
              disabled={!listinoInUso}
              primary
              size="big"
              onClick={() => {
                props.onAvantiClick(
                  moment(sessioneSelezionata.dal).format("YYYY-MM-DD"),
                  giorniOfferta,
                  numSessioniEffettive,
                  numAllievi,
                  listinoInUso.prezzo,
                  listinoInUso.prezzoPag,
                  listinoInUso.prezzoListino,
                  undefined,
                  promoPerc,
                  agencyPerc,
                  listinoInUso.prezzoListinoRiferimento
                );
              }}
            >
              <Icon name="cart" />
              {t("avanti")}
            </Button>
          </Segment>
        </Grid.Column>
      </Grid>
    </Segment>
  );
}

DettaglioOfferta.propTypes = {
  corso: PropTypes.object.isRequired,
  sessioniCorso: PropTypes.object.isRequired,
  datiScuola: PropTypes.object.isRequired,
  onAvantiClick: PropTypes.func.isRequired,
};

export default DettaglioOfferta;
